// 1. React and core hooks
import { useEffect } from 'react';

// 2. External libraries
import { useAuth } from '@clerk/clerk-react';

// 5. Custom hooks and utilities
import { setAuthTokenGetter } from '~/lib/api-mutator';
import { createClerkTokenGetter } from '~/lib/authToken';

/**
 * Keeps the shared HTTP client's token getter in sync with Clerk.
 *
 * Registers an async getter into the mutator module that resolves the current
 * Clerk session token per request. The mutator awaits this getter on every
 * request, so the first authenticated request after a cold load carries a token
 * even though the value is not available synchronously at mount. Returns null
 * when Clerk has not loaded, when signed out, when the token resolves empty, or
 * when resolution fails, so the mutator omits the Authorization header rather
 * than blocking the request.
 *
 * Renders no UI. Must be mounted inside the Clerk provider tree and above any
 * component that fires authenticated API calls.
 */
export function AuthTokenBridge(): null {
  const { getToken, isSignedIn, isLoaded } = useAuth();

  useEffect(() => {
    setAuthTokenGetter(createClerkTokenGetter({ getToken, isLoaded, isSignedIn }));
  }, [getToken, isSignedIn, isLoaded]);

  return null;
}
